import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel } from "react-icons/fa";
import "../css/styles.css";

const BigFiveKenya = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348863/7_Day_big_5_Kenya_safari_lrcl0w.jpg"
            alt="Big Five Kenya Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Kenya Safari</span>
          <h1>7 Day Big Five Kenya Safari</h1>
          <p className="safari-detail-hero-sub">
            Lion, elephant, buffalo, leopard, and rhino — search for Africa's famous Big Five across Kenya's top wildlife areas.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>In Search of the Big Five</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This journey takes you across some of Kenya's most beautiful wildlife regions in search of Africa's famous Big Five — lion, elephant, buffalo, leopard, and rhino.
          </p>
          <p>
            Your adventure begins in the cool highlands of Ol Pejeta Conservancy, a special wildlife sanctuary known for its strong conservation work and rich wildlife. Here you will have the rare chance to see rhinos up close and visit the chimpanzee sanctuary, the only one of its kind in Kenya.
          </p>
          <p>
            From there, the safari continues into the dramatic landscapes of the Great Rift Valley to Lake Nakuru National Park, a beautiful park surrounded by cliffs and forests, home to rhinos, buffalo, giraffes, and many species of birds.
          </p>
          <p>
            The journey then reaches its grand finale in the legendary Masai Mara, one of Africa's greatest wildlife destinations. Vast golden plains stretch endlessly across the horizon where lions, elephants, cheetahs, and countless other animals roam freely.
          </p>
          <p>
            This safari offers a perfect balance of wildlife, landscapes, and unforgettable moments in the heart of Kenya.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Search for the famous Big Five across Kenya's top wildlife areas</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Two nights in Ol Pejeta Conservancy, a leading wildlife sanctuary</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Visit the chimpanzee sanctuary and rhino protection center</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Scenic game drives in Lake Nakuru National Park</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore the vast plains of the Masai Mara</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Excellent opportunities for wildlife photography</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>7 Days / 6 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Ol Pejeta &ndash; Lake Nakuru &ndash; Masai Mara</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>All year round</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Welcome to Kenya. Upon arrival at Jomo Kenyatta International Airport, you will be warmly welcomed by a Nyatwende representative and transferred to your hotel.
              </p>
              <p>
                Depending on your arrival time, you may relax after your journey or explore some of Nairobi's attractions such as the Giraffe Centre or the David Sheldrick Elephant Orphanage.
              </p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Ol Pejeta Conservancy</h3>
              <p>
                After breakfast, depart Nairobi and travel north towards the beautiful Laikipia region. The drive takes you through changing landscapes as the land rises into the cool central highlands.
              </p>
              <p>
                By midday you will arrive at Ol Pejeta Conservancy, a private wildlife sanctuary known for its conservation efforts and rich wildlife.
              </p>
              <p>
                After lunch at your lodge, enjoy an afternoon game drive in the conservancy where you may see elephants, giraffes, lions, and rhinos. Ol Pejeta is also home to the last remaining northern white rhinos in the world, making it one of the most unique wildlife destinations in Africa.
              </p>
              <p className="safari-detail-day-note">Overnight in Ol Pejeta.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in Ol Pejeta Conservancy</h3>
              <p>
                Today is dedicated to exploring Ol Pejeta Conservancy. Morning and afternoon game drives allow you to discover the diverse wildlife that lives here. The conservancy is home to large populations of black and white rhinos, lions, elephants, buffalo, and many antelope species.
              </p>
              <p>
                You may also visit the chimpanzee sanctuary, where rescued chimpanzees live in a protected natural environment. It is the only place in Kenya where chimpanzees can be seen.
              </p>
              <p>
                With its open landscapes and impressive wildlife, Ol Pejeta offers a truly memorable safari experience.
              </p>
              <p className="safari-detail-day-note">Overnight in Ol Pejeta.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Ol Pejeta to Lake Nakuru National Park</h3>
              <p>
                After breakfast, continue your journey south towards the Great Rift Valley and Lake Nakuru National Park. Arrive in time for lunch followed by an afternoon game drive.
              </p>
              <p>
                Lake Nakuru is one of the best places in Kenya to see both black and white rhinos. The park is also home to lions, buffalo, giraffes, zebras, and hundreds of bird species.
              </p>
              <p>
                The scenic lake surrounded by cliffs and forests makes this park especially beautiful.
              </p>
              <p className="safari-detail-day-note">Overnight in Lake Nakuru.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Lake Nakuru to Masai Mara</h3>
              <p>
                After breakfast, depart for the world-famous Masai Mara National Reserve. The journey takes you through rolling hills and rural landscapes before reaching the endless plains of the Mara.
              </p>
              <p>
                You will arrive in time for lunch at your lodge or camp followed by an afternoon game drive. The Masai Mara is known for its incredible concentration of wildlife and its dramatic landscapes where lions, elephants, giraffes, and antelope roam freely.
              </p>
              <p className="safari-detail-day-note">Overnight in the Masai Mara.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Full Day in the Masai Mara</h3>
              <p>
                Today is a full day dedicated to exploring the Masai Mara. Morning and afternoon game drives allow you to discover the rich wildlife that lives here. Lions rest in the tall grass while cheetahs scan the plains for prey. Herds of zebras and antelopes move across the savannah as elephants slowly wander through the landscape.
              </p>
              <p>
                During the migration season, the Mara becomes home to the great herds of wildebeest and zebras that travel across the plains in search of fresh grass.
              </p>
              <p>
                You may also choose to visit a Maasai cultural village to learn about the traditions and daily life of the Maasai people, or experience a sunrise hot air balloon safari, floating quietly above the Mara plains as the wildlife awakens below.
              </p>
              <p className="safari-detail-day-note">Overnight in the Masai Mara.</p>
            </div>
          </div>

          {/* Day 7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 7 — Masai Mara to Nairobi</h3>
              <p>
                After breakfast, enjoy the final views of the Mara before beginning the journey back to Nairobi.
              </p>
              <p>
                You will arrive in the afternoon where you will be dropped off at your hotel or transferred to the airport for your onward journey.
              </p>
              <p className="safari-detail-day-note">
                Your safari ends here, but the memories of Kenya's landscapes and wildlife will stay with you long after the journey ends.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Nairobi */}
        <h3 className="safari-detail-accom-region-title">Nairobi</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">City Hotels</div>
            <ul>
              <li><FaHotel /> Tamarind Tree Hotel</li>
              <li><FaHotel /> Four Points by Sheraton Nairobi Airport</li>
            </ul>
          </div>
        </div>

        {/* Ol Pejeta */}
        <h3 className="safari-detail-accom-region-title">Ol Pejeta Conservancy</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodge</div>
            <ul>
              <li><FaHotel /> Sweetwaters Serena Camp</li>
            </ul>
          </div>
        </div>

        {/* Lake Nakuru */}
        <h3 className="safari-detail-accom-region-title">Lake Nakuru</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Lake Nakuru Sopa Lodge</li>
              <li><FaHotel /> Sarova Lion Hill Game Lodge</li>
            </ul>
          </div>
        </div>

        {/* Masai Mara */}
        <h3 className="safari-detail-accom-region-title">Masai Mara</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Enkorok Mara Camp</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Mara Sopa Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Mara Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Nairobi</li>
              <li>Transport in a comfortable safari vehicle with a professional guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as Maasai village visits or hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This safari can easily be tailored to suit your travel style. You may upgrade lodges, extend your stay, or combine the safari with a relaxing beach holiday on Kenya's beautiful coast. Tell us your travel plans and we will create a journey designed just for you.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=7 Day Big Five Kenya Safari" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BigFiveKenya;
